import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Button, MessagePlugin, TableRowData, Tag } from 'tdesign-react';

import { IFormItemProps } from '@/components';
import { FormTitle } from '@/components/form/materials/Title';
import DialogForm from '@/components/form/wrapper/DialogForm';
import SimpleSortTable from '@/components/table/simpleSortTable';
import { BotDataManagerType } from '@/const/promotion';
import { eventPool } from '@/event/eventPool';
import { useEventBus } from '@/hooks/useEventBus';
import { IBot, ISpace, IStory } from '@/interface';
import { IPlazaItemInput } from '@/interface/promotion';
import BotCard from '@/pages/index/bot';
import { SpaceCard } from '@/pages/promotion/cards/spaceCard';
import { formKeys, ManageBotSelectOptions } from '@/pages/promotion/info/custom-form/custom-index/config';
import { fetchPlazaItemDetail, modifyPlazaItemDetail } from '@/services/plaza';
import { orgBotData, OrgDataType } from '@/services/promotionData';
import { getMaterial } from '@/utils/material';

import { botFormConfig, formStyle } from './form-config/common';
import styles from './index.module.less';

interface IProps {
  data: IPlazaItemInput[],
  onChange: (data: IPlazaItemInput) => void;
  onMove: (id: string) => void;
  onSort: (data: IPlazaItemInput[]) => void;
}
const MAX_TABLE_WIDTH = 1200;

const List: React.FC<IProps> = (props) => {
  const {
    data = [],
    onChange,
    onMove,
    onSort,
  } = props;
  const tableRef = useRef(null);
  const formRef = useRef<any>();
  const columns = [
    {
      colKey: formKeys.managerType,
      title: '类型',
      width: MAX_TABLE_WIDTH * 0.05,
      cell: ({ row }: { row: TableRowData }) => {
        const cname = ManageBotSelectOptions?.find(item => item.value === row?.[formKeys.managerType])?.label;
        return (
          <div><Tag theme="primary">{cname}</Tag></div>
        );
      },
    },
    {
      colKey: 'cover',
      title: '合集类型',
      width: MAX_TABLE_WIDTH * 0.15,
      minWidth: 200,
      cell: ({ row }: { row: TableRowData }) => {
        const data = row?.managerType === BotDataManagerType.BOTDATAMANAGERTYPE_COLLECTION ? {
          ...row,
        } : {
          ...(row?.space?.opt || row?.space),
        };
        return  <SpaceCard key={row?.id} data={data} />;
      },
    },
    {
      colKey: 'bots',
      title: '选择角色',
      width: MAX_TABLE_WIDTH * 0.35,
      cell: ({ row }: { row: TableRowData }) => {
        const items = row?.bots?.bots || [];
        if (isEmpty(items)) {
          return '--';
        }
        return (
          <div className={styles.stories}>
            {items.map((item: Partial<IStory | IBot>) => (
              <BotCard key={item.id} item={item as IBot} showBtn={false} />
            ))}
          </div>
        );
      },
    },
    {
      colKey: 'op',
      title: '操作',
      width: MAX_TABLE_WIDTH * 0.1,
      align: 'center' as 'center',
      cell: ({ row }: { row: TableRowData }) => (
        <div className={styles.options}>
          {row?.managerType !== BotDataManagerType.BOTDATAMANAGERTYPE_RECENT_VISIT && <Button
            onClick={() => handleEdit(row)}
            theme="default"
            variant="text"
          >
            编辑
          </Button>}
          <Button
            theme="default"
            variant="text"
            onClick={() => onMove(row.id)}
          >
            移除
          </Button>
        </div>
      ),
    },
  ];

  const handleEdit = async (row: TableRowData) => {
    let rowData = { ...row };
    if (row?.id) {
      const resData = await fetchDetail(row.id);
      rowData = orgBotData(OrgDataType.output, resData);
    }
    setFormData(rowData as IPlazaItemInput);
    setVisible(true);
  };

  const fetchDetail = async (id: string) => {
    const fetchItemDetail = fetchPlazaItemDetail;
    const { code, data, message } = await fetchItemDetail(id);
    if (code !== 0) {
      MessagePlugin.error({
        content: message || '获取失败，请重试',
      });
    }
    return data;
  };

  const getData = (data: IPlazaItemInput[]) => {
    onSort(data);
  };

  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState<IPlazaItemInput>({});
  const [materialMap, setMaterialMap] = useState<Record<string, string>>({});

  useEffect(() => {
    setMaterialMap(formData?.materialMap || {});
  }, [formData?.materialMap]);

  useEventBus(eventPool.eventsTypes.MATERIAL_CHANGE, (payload: any) => {
    const result = getMaterial(payload, { ...materialMap });
    setMaterialMap(result);
  });

  const onSubmit = async () => {
    const postData = { ...formData, materialMap };
    if (isEmpty(postData.bots) && postData.managerType !== BotDataManagerType.BOTDATAMANAGERTYPE_RECENT_VISIT) {
      MessagePlugin.error('请选择角色');
      return;
    }
    const modifyItemDetail = modifyPlazaItemDetail;
    const { code, data, message } = await modifyItemDetail({ ...postData });
    if (code !== 0) {
      MessagePlugin.error({
        content: message || '保存失败，请重试',
      });
      return false;
    }
    onChange({ ...postData, id: data.id });
  };

  const handleClose = () => {
    setFormData({});
    setVisible(false);
  };

  const onValuesChange = (content: IPlazaItemInput) => {
    const style = formStyle?.[
      content?.managerType
      || formData?.managerType
      || BotDataManagerType.BOTDATAMANAGERTYPE_DEFAULT
    ];
    let postData = { ...formData, ...content, style };
    const lastSpaceId = formData?.space?.id || (formData?.space as { opt?: ISpace })?.opt?.id;
    const newSpaceId = postData?.space?.id || (postData?.space as { opt?: ISpace })?.opt?.id;
    if (lastSpaceId !== newSpaceId) {
      postData.stories = {};
      postData.bots = {};
      formRef.current?.formRef?.form.setFieldsValue({ bots: {} });
      formRef.current?.formRef?.form.setFieldsValue({ stories: {} });
    }
    if (postData?.managerType !== formData?.managerType) {
      postData = { managerType: postData?.managerType, style };
      postData.stories = {};
      postData.bots = {};
      formRef.current?.formRef?.form.setFieldsValue({ bots: {} });
      formRef.current?.formRef?.form.setFieldsValue({ stories: {} });
    }
    setFormData(postData);
  };

  const orgConfigs = (
    configs: IFormItemProps[],
    formData: IPlazaItemInput,
    data: IPlazaItemInput[],
  ) => {
    const hasBanner = data?.some(e => e.managerType === BotDataManagerType.BOTDATAMANAGERTYPE_BANNER);
    return (
      configs?.map((e) => {
        if (e?.props === formKeys.stories || e?.props === formKeys.bots) {
          return {
            ...e,
            uiOptions: {
              ...e?.uiOptions,
              parentData: formData,
            },
          };
        }
        if (e?.props === formKeys.managerType) {
          const options = ManageBotSelectOptions;
          const selectOptions = options?.map((e) => {
            if (e.value === BotDataManagerType.BOTDATAMANAGERTYPE_BANNER && hasBanner) {
              return { ...e, disabled: true };
            }
            return e;
          });
          return {
            ...e,
            uiOptions: {
              ...e?.uiOptions,
              fetchOptions: async () => ({
                list: selectOptions,
              }),
            },
          };
        }
        return e;
      })
    );
  };

  const handleAddItem = () => {
    setVisible(true);
  };

  const addItem = () => (
    <div className={styles.addWrap}>
      <Button style={{ width: '100%', height: 40 }} variant="outline" theme="primary" ghost onClick={handleAddItem}>
        新增配置
      </Button>
    </div>
  );

  const refactorFieldsConfig = (
    config: IFormItemProps[],
    values: IPlazaItemInput,
    data: IPlazaItemInput[],
  ) => orgConfigs(config, values, data);

  const fieldsConfig = botFormConfig;

  return (
    <>
      <SimpleSortTable
        cRef={tableRef}
        columnsConfig={columns}
        dataList={data}
        dragWidth={100}
        getData={getData}
        extendsConfig={{}}
        tableConfig={{ bottomContent: <>{addItem()}</> }}
      />
      <DialogForm
        cRef={formRef}
        fieldsConfig={fieldsConfig[formData?.managerType
          || BotDataManagerType.BOTDATAMANAGERTYPE_BANNER]}
        onSubmit={onSubmit}
        refactorFieldsConfig={config => refactorFieldsConfig(config, formData, data)}
        onClose={handleClose}
        isVisible={visible}
        formData={{
          ...formData, space: {
            label: formData?.space?.name,
            value: formData?.space?.id,
            opt: formData?.space,
          },
          name: formData?.name || formData?.space?.name,
          intro: formData?.intro || formData?.space?.intro,
        }}
        formConfig={{
          layout: 'vertical',
          labelWidth: 120,
          labelAlign: 'top',
          onValuesChange,
        }}
        layOutConfig={{
          paddingBottom: 30,
          maxHeight: '75vh',
        }}
        dialogConfig={{
          header: (
            <FormTitle
              title={'新增配置'}
              replaceObject={{}}
            />
          ),
          style: { width: '90%' },
          destroyOnClose: true,
        }}
      />
    </>
  );
};

export default List;
